import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { alpha, Box, Collapse, Divider, List, ListItemButton, ListItemIcon, ListItemText, styled, Typography } from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WorkHistoryIcon from "@mui/icons-material/WorkHistory";
import PeopleIcon from "@mui/icons-material/People";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import { pageRoutes } from "../../../helpers/constant/pageRoutes";
import ArticleIcon from "@mui/icons-material/Article";
import { ExitToApp } from "@mui/icons-material";
import AcUnitIcon from "@mui/icons-material/AcUnit";

export const sidebarMenuList = [
  {
    href: `${pageRoutes.companies.path}`,
    title: "Companies",
    icon: <BusinessOutlinedIcon fontSize="small" />,
  },
  {
    href: `${pageRoutes.jobSeeker.path}`,
    title: "Job seekers",
    icon: <PeopleIcon fontSize="small" />,
  },
  {
    href: `${pageRoutes.jobs.path}`,
    title: "Jobs",
    icon: <WorkHistoryIcon fontSize="small" />,
  },
  {
    href: `${pageRoutes.templates.path}`,
    title: "Templates",
    icon: <ArticleIcon fontSize="small" />,
  },
  {
    href: `${pageRoutes.logs.path}`,
    title: "Logs",
    icon: <AcUnitIcon fontSize="small" />,
  },
];

const ListItemButtonStyled = styled(ListItemButton)(({ theme }) => ({
  borderRadius: theme.spacing(1.25),
  marginBottom: theme.spacing(0.75),
  padding: theme.spacing(1.15, 1.5),
  color: theme.palette.text.secondary,
  transition: theme.transitions.create(["background-color", "box-shadow", "color"], {
    duration: theme.transitions.duration.shortest,
  }),
  "& .MuiListItemIcon-root": {
    minWidth: theme.spacing(4.5),
    color: "inherit",
  },
  "& .MuiListItemText-primary": {
    fontSize: 14,
    fontWeight: 650,
  },
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.07),
    color: theme.palette.primary.main,
  },
  "&.Mui-selected": {
    backgroundColor: alpha(theme.palette.primary.main, 0.11),
    boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`,
    color: theme.palette.primary.main,
  },
  "&.Mui-selected:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.15),
  },
}));

const hasChildren = (menu) => {
  const { subMenus: children } = menu || {};
  return Array.isArray(children);
};

const DashboardSidebar = () => {
  const handleLogOut = () => {
    if (window.employerLogOut) window.employerLogOut();
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, px: 2, py: 2.5 }}>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", py: 2 }}>
        <Typography variant="overline" color="text.tertiary" sx={{ display: "block", px: 1.5, mb: 1 }}>
          Workspace
        </Typography>
        <List component="nav" disablePadding>
          {sidebarMenuList.map((menu, index) => {
            return <MenuItem key={index} menu={menu} />;
          })}
        </List>
      </Box>
      <Divider sx={{ mb: 1.5 }} />
      <List disablePadding>
        <ListItemButtonStyled onClick={handleLogOut} sx={{ mb: 0, color: "error.main" }}>
          <ListItemIcon>
            <ExitToApp fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Sign out" />
        </ListItemButtonStyled>
      </List>
    </Box>
  );
};

const MenuItem = ({ menu }) => {
  const Component = hasChildren(menu) ? MultiLevel : SingleLevel;
  return <Component menu={menu} />;
};

const SingleLevel = ({ menu }) => {
  const { pathname } = useLocation();
  const active = pathname === menu.href || (menu.href === pageRoutes.jobs.path && pathname === "/dashboard") || pathname.startsWith(`${menu.href}/`);

  return (
    <Link to={menu.href}>
      <ListItemButtonStyled selected={active}>
        <ListItemIcon>{menu.icon}</ListItemIcon>
        <ListItemText primary={menu.title} />
      </ListItemButtonStyled>
    </Link>
  );
};

const MultiLevel = ({ menu }) => {
  const { subMenus: children } = menu;
  const [open, setOpen] = useState(false);

  const handleClick = () => {
    setOpen((prev) => !prev);
  };

  return (
    <>
      <ListItemButtonStyled onClick={handleClick}>
        <ListItemIcon>{menu.icon}</ListItemIcon>
        <ListItemText primary={menu.title} />
        {open ? <ExpandLessIcon fontSize={"small"} color={"action"} /> : <ExpandMoreIcon fontSize={"small"} color={"action"} />}
      </ListItemButtonStyled>

      <Collapse in={open} timeout="auto">
        <List component="div" disablePadding>
          {children.map((child, key) => (
            <MenuItem key={key} menu={child} />
          ))}
        </List>
      </Collapse>
    </>
  );
};

export default DashboardSidebar;
